import type { ScopeNode, ScopeSelectionProjection } from "../types/scope";
import type { CmdbToolsByOrgScope } from "../services/cmdbToolsApi";



function walk(nodes: ScopeNode[], visit: (n: ScopeNode) => void) {
  for (const node of nodes) {
    visit(node);
    if (node.children?.length) walk(node.children, visit); 
  }
}

function findNode(nodes: ScopeNode[], predicate: (n: ScopeNode) => boolean): ScopeNode | null {
  for (const node of nodes) {
    if (predicate(node)) return node;
    if (node.children?.length) {
      const found = findNode(node.children, predicate);
      if (found) return found;
    }
  }
  return null;
}


export function findFirstScopeIdByType(
  tree: ScopeNode[],
  type: ScopeNode["type"] = "application",
): string | null {
  return findNode(tree, (n) => n.type === type)?.id ?? null;
}

export function findScopeIdByAppCode(tree: ScopeNode[], appCode: string): string | null {
  return findNode(tree, (n) => n.type === "application" && n.appCode === appCode)?.id ?? null;
}

export function findScopeIdByAppName(tree: ScopeNode[], appName: string): string | null {
  return findNode(tree, (n) => n.type === "application" && n.name === appName)?.id ?? null;
}

/** Returns rootId plus every descendant id, so selecting an Application also
 * checks all of its Assets in the tree UI. */
export function collectScopeIdsUnder(tree: ScopeNode[], rootId: string): string[] {
  const root = findNode(tree, (n) => n.id === rootId);
  if (!root) return [rootId];
  const ids: string[] = [];
  walk([root], (n) => ids.push(n.id));
  return ids;
}


export function ancestorScopeIds(tree: ScopeNode[], ids: string[]): string[] {
  const targets = new Set(ids);
  const ancestors = new Set<string>();
  const visit = (node: ScopeNode, path: string[], underTarget: boolean) => {
    const isTarget = targets.has(node.id);
    if (isTarget && !underTarget) path.forEach((id) => ancestors.add(id));
    const nextPath = [...path, node.id];
    for (const child of node.children ?? []) visit(child, nextPath, underTarget || isTarget);
  };
  for (const node of tree) visit(node, [], false);
  return Array.from(ancestors);
}

function selectedNodes(tree: ScopeNode[], selectedIds: string[]): ScopeNode[] {
  const idSet = new Set(selectedIds);
  const matches: ScopeNode[] = [];
  walk(tree, (n) => {
    if (idSet.has(n.id)) matches.push(n);
  });
  return matches;
}

export function selectedScopeAssetNames(tree: ScopeNode[], selectedIds: string[]): string[] {
  const names = new Set<string>();
  for (const node of selectedNodes(tree, selectedIds)) {
    if (node.type === "asset") names.add(node.name);
    node.assetNames?.forEach((n) => names.add(n));
  }
  return Array.from(names);
}

export function selectedScopeApplicationCodes(tree: ScopeNode[], selectedIds: string[]): string[] {
  const codes = new Set<string>();
  for (const node of selectedNodes(tree, selectedIds)) {
    if (node.type === "application" && node.appCode) codes.add(node.appCode);
  }
  return Array.from(codes);
}

export function selectedScopeApplicationNames(tree: ScopeNode[], selectedIds: string[]): string[] {
  const names = new Set<string>();
  for (const node of selectedNodes(tree, selectedIds)) {
    if (node.type === "application") names.add(node.name);
  }
  return Array.from(names);
}

/** Ids of the selected Application nodes (what the STOC dashboards send as `applicationIds`). */
export function selectedScopeApplicationIds(tree: ScopeNode[], selectedIds: string[]): string[] {
  return selectedNodes(tree, selectedIds)
    .filter((n) => n.type === "application")
    .map((n) => n.id);
}

/** Ids of the Organization nodes that are selected or that contain a selected node, so picking
 * only an Application still reports the organization it belongs to (`organizationIds`). */
export function selectedScopeOrganizationIds(tree: ScopeNode[], selectedIds: string[]): string[] {
  const idSet = new Set(selectedIds);
  const orgIds: string[] = [];
  const hasSelection = (node: ScopeNode): boolean =>
    idSet.has(node.id) || (node.children ?? []).some(hasSelection);
  walk(tree, (node) => {
    if (node.type === "org" && hasSelection(node)) orgIds.push(node.id);
  });
  return orgIds;
}

export function allApplicationNames(tree: ScopeNode[]): string[] {
  const names = new Set<string>();
  walk(tree, (node) => {
    if (node.type === "application") names.add(node.name);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

export function allApplicationScopeIds(tree: ScopeNode[]): string[] {
  const ids: string[] = [];
  walk(tree, (node) => {
    if (node.type === "application") ids.push(node.id);
  });
  return ids;
}

export function selectedScopeTypes(tree: ScopeNode[], selectedIds: string[]): ScopeNode["type"][] {
  const types = new Set<ScopeNode["type"]>();
  for (const node of selectedNodes(tree, selectedIds)) types.add(node.type);
  return Array.from(types);
}

/** Projects selected scope nodes into asset names, application codes, and application names in a single traversal. */
export function projectScopeSelection(tree: ScopeNode[], selectedIds: string[]): ScopeSelectionProjection {
  const assetNames = new Set<string>();
  const appCodes = new Set<string>();
  const appNames = new Set<string>();

  for (const node of selectedNodes(tree, selectedIds)) {
    if (node.type === "asset") assetNames.add(node.name);
    node.assetNames?.forEach((n) => assetNames.add(n));
    if (node.type === "application") {
      appNames.add(node.name);
      if (node.appCode) appCodes.add(node.appCode);
    }
  }

  return {
    assetNames: Array.from(assetNames),
    appCodes: Array.from(appCodes),
    appNames: Array.from(appNames),
  };
}

/** Flattens the tree so a scope filter can start with everything selected. */
export function collectAllScopeIds(nodes: ScopeNode[]): string[] {
  const ids: string[] = [];
  walk(nodes, (n) => ids.push(n.id));
  return ids;
}

/** Buckets every node in the tree by type for the Tool filter's by-org lookup. */
export function collectOrgScopeByType(nodes: ScopeNode[]): CmdbToolsByOrgScope {
  const scope: Required<CmdbToolsByOrgScope> = {
    groupIds: [],
    organizationIds: [],
    divisionIds: [],
    departmentIds: [],
    applicationIds: [],
  };
  walk(nodes, (node) => {
    if (node.name === "Unmapped") return;
    switch (node.type) {
      case "group":
        scope.groupIds.push(node.id);
        break;
      case "org":
        scope.organizationIds.push(node.id);
        break;
      case "division":
        scope.divisionIds.push(node.id);
        break;
      case "department":
        scope.departmentIds.push(node.id);
        break;
      case "application":
        scope.applicationIds.push(node.id);
        break;
    }
  });
  return scope;
}
